import { DatePipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TableComponent } from '@custom-components/general/table/table.component';
import {
  FilterGroup,
  TableFilterBarComponent,
} from '@custom-components/general/table-filter-bar/table-filter-bar.component';
import { TableColumnConfig, TableColumnType } from '@core/models/skeleton.model';
import { TagStateOf } from '@core/helpers';
import {
  AlarmRow,
  capitalizePriority,
  CLOSED_REASONS,
  ClosedReason,
  ClosedStatusResponse,
  DeviceModel,
  EventBasedStatus,
  LatestStatusResponse,
  StaticStatus,
  SubDevice,
} from '@core/models';
import { StatusService } from '@core/services/status.service';
import { AlarmTypeCatalogService } from '@core/services/alarm-type-catalog.service';
import { displayAlarmTypeName, isLogBackedAlarmType } from '@core/helpers';
import { StatusStore } from '@root/app/store/status.store';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { ConfirmCloseStatusDialogComponent } from '../confirm-close-status-dialog/confirm-close-status-dialog.component';
import { ActorNamePipe, actorKindOf } from '@core/pipes/actor-name.pipe';

interface AlarmDisplayRow extends Record<string, unknown> {
  typeName: string;
  rawTypeName: string;
  priority: 'Low' | 'Medium' | 'High';
  openedAt: string;
  closedAt: string;
  closedBy: string;
  closedReason: string;
  state: 'Open' | 'Closed';
  age: string;
  canClose: boolean;
  deviceId: string;
  subDeviceType?: string;
}

function knownClosedReason(reason: string | null | undefined): ClosedReason | undefined {
  return CLOSED_REASONS.find(known => known === reason);
}

@Component({
  selector: 'xemex-device-alarms-tab',
  imports: [
    MatIconModule,
    MatButtonModule,
    TableComponent,
    TableFilterBarComponent,
    TranslateModule,
  ],
  templateUrl: './device-alarms-tab.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [TranslatePipe, DatePipe, ActorNamePipe],
})
export class DeviceAlarmsTabComponent {
  deviceDetail = input<DeviceModel>();
  // For a page that has the device's latest status without its detail.
  latestStatus = input<LatestStatusResponse>();
  // Closed alarms belong to a device, not to its latest status: a device whose alarms were all
  // closed has no latest status left, but its closed alarms are still to be listed.
  deviceId = input<string>();
  isLoading = input<boolean>(false);
  alarmClosed = output<void>();

  readonly statusStore = inject(StatusStore);
  readonly dialog = inject(MatDialog);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly translateService = inject(TranslatePipe);
  private readonly datePipe = inject(DatePipe);
  private readonly actorName = inject(ActorNamePipe);
  private readonly statusService = inject(StatusService);
  private readonly alarmTypeCatalog = inject(AlarmTypeCatalogService);
  private readonly destroyRef = inject(DestroyRef);

  /** Renders the human label for a raw catalog key (e.g. "fraud.44" -> "Fraud Detection Event:
   * Meter cover removed") instead of leaking the raw key into the UI. Falls back to translating
   * row.typeName (an i18n key like "status.names.foo") for the rare case a row has no
   * catalogKey at all. */
  private displayTypeName(row: AlarmRow): string {
    const key = row.catalogKey;
    if (!key) return this.translateService.transform(row.typeName) || row.typeName;
    return displayAlarmTypeName(
      key,
      k => this.alarmTypeCatalog.label(k),
      k => this.translateService.transform(k),
    );
  }

  readonly columns: TableColumnConfig[] = [
    {
      field: 'typeName',
      label: 'devices.alarms.table.type',
      type: TableColumnType.Text,
      bold: true,
      width: '17%',
    },
    {
      field: 'priority',
      label: 'devices.alarms.table.priority',
      type: TableColumnType.Tag,
      stateField: 'priorityState',
      width: '9%',
    },
    {
      field: 'openedAt',
      label: 'devices.alarms.table.openedAt',
      type: TableColumnType.Text,
      width: '12%',
    },
    {
      field: 'closedAt',
      label: 'devices.alarms.table.closedAt',
      type: TableColumnType.Text,
      width: '12%',
    },
    {
      field: 'state',
      label: 'devices.alarms.table.state',
      type: TableColumnType.Text,
      width: '8%',
    },
    {
      field: 'closedBy',
      label: 'devices.alarms.table.closedBy',
      type: TableColumnType.Text,
      width: '14%',
    },
    {
      field: 'closedReason',
      label: 'devices.alarms.table.closedReason',
      type: TableColumnType.Text,
      width: '11%',
    },
    { field: 'age', label: 'devices.alarms.table.age', type: TableColumnType.Text, width: '7%' },
  ];

  private readonly openRows = signal<AlarmRow[]>([]);
  private readonly closedRows = signal<AlarmRow[]>([]);
  closedLoading = signal(false);

  readonly query = signal('');
  readonly activeFilters = signal<Record<string, string[]>>({});

  readonly filterGroups = computed<FilterGroup[]>(() => {
    const types = [
      ...new Set([...this.openRows(), ...this.closedRows()].map(r => this.displayTypeName(r))),
    ];
    return [
      {
        key: 'type',
        label: this.translateService.transform('devices.alarms.table.type'),
        options: types,
      },
      {
        key: 'priority',
        label: this.translateService.transform('devices.alarms.table.priority'),
        options: ['High', 'Medium', 'Low'],
      },
      {
        key: 'state',
        label: this.translateService.transform('devices.alarms.table.state'),
        options: ['Open', 'Closed'],
      },
    ];
  });

  private readonly filteredRows = computed<AlarmRow[]>(() => {
    const q = this.query().toLowerCase();
    const typeFilters = this.activeFilters()['type'] ?? [];
    const priorityFilters = this.activeFilters()['priority'] ?? [];
    const stateFilters = this.activeFilters()['state'] ?? [];

    return [...this.openRows(), ...this.closedRows()]
      .filter(row => {
        const typeName = this.displayTypeName(row);
        if (q && !typeName.toLowerCase().includes(q)) return false;
        if (typeFilters.length && !typeFilters.includes(typeName)) return false;
        if (priorityFilters.length && !priorityFilters.includes(row.priority)) return false;
        if (stateFilters.length && !stateFilters.includes(row.state)) return false;
        return true;
      })
      .sort((a, b) => {
        if (a.state !== b.state) return a.state === 'Open' ? -1 : 1;
        return (b.openedAt?.getTime() ?? 0) - (a.openedAt?.getTime() ?? 0);
      });
  });

  readonly tableData = computed<AlarmDisplayRow[]>(() =>
    this.filteredRows().map(row => {
      const priority = row.catalogKey ? this.priorityFor(row.catalogKey) : row.priority;
      return {
        typeName: this.displayTypeName(row),
        rawTypeName: row.catalogKey ?? row.typeName,
        priority,
        priorityState: TagStateOf.alarmPriority(priority),
        openedAt: this.datePipe.transform(row.openedAt, 'd MMM HH:mm') ?? '–',
        closedAt: this.datePipe.transform(row.closedAt, 'd MMM HH:mm') ?? '–',
        closedBy: this.closedByLabel(row.closedBy),
        closedReason: row.closedReason
          ? this.translateService.transform(`devices.alarms.closedReason.${row.closedReason}`)
          : '–',
        state: row.state,
        age: this.getAge(row.openedAt),
        canClose: row.canClose,
        deviceId: row.deviceId,
        subDeviceType: row.subDeviceType,
      };
    }),
  );

  private lastLoadedDeviceId: string | undefined;

  constructor() {
    effect(() => {
      const status = this.latestStatus() ?? this.deviceDetail()?.internalDeviceStatus;
      const knownDeviceId = status
        ? String((status.item as StaticStatus).device_id)
        : (this.deviceId() ?? this.deviceDetail()?.deviceId);

      if (knownDeviceId && this.lastLoadedDeviceId !== knownDeviceId) {
        this.lastLoadedDeviceId = knownDeviceId;
        this.loadClosedAlarms(knownDeviceId);
      }

      if (!status) {
        this.openRows.set([]);
        return;
      }

      const deviceId = String((status.item as StaticStatus).device_id);

      if (status.type === 'event') {
        this.statusStore.getChannelConfigurationsMethod([Number(deviceId)]);
      }

      this.openRows.set(this.buildOpenRows(status, deviceId));
    });
  }

  private buildOpenRows(status: LatestStatusResponse, deviceId: string): AlarmRow[] {
    if (status.type === 'event') {
      const eventItem = status.item as EventBasedStatus;
      return eventItem.underlying_devices.map(subDevice => ({
        typeName: this.getSubDeviceStatusName(subDevice, Number(deviceId)),
        catalogKey: subDevice.name,
        priority: this.priorityFor(subDevice.name),
        openedAt: new Date(subDevice.reported_at * 1000),
        state: 'Open' as const,
        canClose: true,
        deviceId,
        subDeviceType: subDevice.type,
      }));
    }

    const staticItem = status.item as StaticStatus;
    const catalogKey = this.getStaticCatalogKey(staticItem);
    return [
      {
        typeName: this.getStatusName(status),
        catalogKey,
        priority: this.priorityFor(catalogKey),
        openedAt: new Date(staticItem.start_interval_time * 1000),
        state: 'Open' as const,
        canClose: true,
        deviceId,
        subDeviceType: this.getStaticStatusId(staticItem),
      },
    ];
  }

  /** Resolves the real priority via the central AlarmTypeCatalogService, falling back to
   * 'Medium' for unknown/unloaded catalog keys (see DoD: onbekende prio -> neutrale tag
   * zonder crash) — mirrors the closed-row mapping in buildClosedRows below. */
  private priorityFor(catalogKey: string | undefined): 'Low' | 'Medium' | 'High' {
    if (!catalogKey) return 'Medium';
    return capitalizePriority(this.alarmTypeCatalog.priority(catalogKey) ?? 'medium');
  }

  /** Same "lowest status_code wins" selection as getStatusName(), but returns the raw
   * catalog key (status_name) instead of an i18n key, for priority lookup. */
  private getStaticCatalogKey(staticItem: StaticStatus): string | undefined {
    const item = staticItem.items?.reduce(
      (x, y) =>
        Number.parseFloat(x.status_code ?? '0') < Number.parseFloat(y.status_code ?? '0') ? x : y,
      staticItem.items[0],
    );
    return item?.status_name;
  }

  /** Same "lowest status_code wins" selection as getStaticCatalogKey(), but returns the
   * Cosmos-backed status_id the close endpoint requires as `status_id`. */
  private getStaticStatusId(staticItem: StaticStatus): string | undefined {
    const item = staticItem.items?.reduce(
      (x, y) =>
        Number.parseFloat(x.status_code ?? '0') < Number.parseFloat(y.status_code ?? '0') ? x : y,
      staticItem.items[0],
    );
    return item?.status_id;
  }

  private loadClosedAlarms(deviceId: string): void {
    this.closedLoading.set(true);
    this.statusService
      .getClosedAlarmStatuses(deviceId)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(result => {
        this.closedLoading.set(false);
        if (result) {
          this.closedRows.set(this.buildClosedRows(result.items));
        }
      });
  }

  private buildClosedRows(items: ClosedStatusResponse[]): AlarmRow[] {
    return items.map(cs => ({
      typeName: cs.status,
      catalogKey: cs.status,
      priority: this.priorityFor(cs.status),
      openedAt: cs.opened_at == null ? undefined : new Date(cs.opened_at * 1000),
      closedAt: cs.closed_at == null ? undefined : new Date(cs.closed_at * 1000),
      closedBy: cs.closed_by,
      closedReason: knownClosedReason(cs.closed_reason),
      state: 'Closed' as const,
      canClose: false,
      deviceId: cs.device_id,
    }));
  }

  /** An alarm the system closed by itself says so in full; anyone else gets the shared actor label. */
  private closedByLabel(actor: string | undefined): string {
    if (!actor) return '–';
    if (actorKindOf(actor) === 'system') {
      return this.translateService.transform('devices.alarms.closedBySystem');
    }
    return this.actorName.transform(actor);
  }

  private getAge(openedAt: Date | undefined): string {
    if (!openedAt) return '';
    const diffMs = Date.now() - openedAt.getTime();
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    if (hours < 24) return `${hours} hrs`;
    return `${Math.floor(hours / 24)} days`;
  }

  onCloseStatus(row: AlarmDisplayRow): void {
    const dialogRef = this.dialog.open(ConfirmCloseStatusDialogComponent, {
      data: {
        statusName: row.typeName,
        resetsDeviceLog: isLogBackedAlarmType(row.rawTypeName),
      },
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed === true) {
        this.closeAlarm(row.deviceId, row.subDeviceType);
      }
    });
  }

  private closeAlarm(deviceId: string, statusId?: string): void {
    this.statusService
      .closeAlarmStatus(deviceId, statusId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this._snackBar.open(this.translateService.transform('devices.alarms.closeError'), '', {
            horizontalPosition: 'right',
            verticalPosition: 'top',
            duration: 3000,
          });
          return of(null);
        }),
      )
      .subscribe(response => {
        if (response !== null) {
          this._snackBar.open(this.translateService.transform('devices.alarms.closeSuccess'), '', {
            horizontalPosition: 'right',
            verticalPosition: 'top',
            duration: 3000,
          });
          this.loadClosedAlarms(deviceId);
          this.alarmClosed.emit();
        }
      });
  }

  private getSubDeviceStatusName(subDevice: SubDevice, deviceId: number): string {
    const config = this.statusStore.getChannelConfiguration(deviceId);
    if (config?.configuration?.channels) {
      const channel = config.configuration.channels.find(
        x => x.channelNumber === Number.parseInt(subDevice.type.charAt(0)),
      );
      const deviceName = channel?.deviceConfiguration?.devices?.[0]?.deviceName;
      if (deviceName) return `status.device_name.${deviceName}`;
    }
    return subDevice.name ? `status.names.${subDevice.name}` : '';
  }

  private getStatusName(response: LatestStatusResponse): string {
    if (response.type === 'event') {
      const eventItem = response.item as EventBasedStatus;
      const mostRecent = eventItem.underlying_devices.reduce(
        (x, y) => (x.reported_at > y.reported_at ? x : y),
        eventItem.underlying_devices[0],
      );
      const type = mostRecent?.type;
      const fallback = eventItem.underlying_devices[0]?.name;
      if (!type) return fallback ? `status.names.${fallback}` : '';

      const config = this.statusStore.getChannelConfiguration(response.item.device_id);
      if (!config?.configuration?.channels) return fallback ? `status.names.${fallback}` : '';

      const channel = config.configuration.channels.find(
        x => x.channelNumber === Number.parseInt(type.charAt(0)),
      );
      if (!channel?.deviceConfiguration?.devices?.length)
        return fallback ? `status.names.${fallback}` : '';

      const deviceName = channel.deviceConfiguration.devices[0].deviceName;
      return deviceName
        ? `status.device_name.${deviceName}`
        : fallback
          ? `status.names.${fallback}`
          : '';
    }

    if (response.type === 'static') {
      const staticItems = (response.item as StaticStatus).items;
      const item = staticItems?.reduce(
        (x, y) =>
          Number.parseFloat(x.status_code ?? '0') < Number.parseFloat(y.status_code ?? '0') ? x : y,
        staticItems[0],
      );
      return item?.status_name ? `status.names.${item.status_name}` : '';
    }

    return '';
  }
}
